import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

const translations = {
  fr: {
    nav: { properties: "Nos logements", concierge: "Conciergerie", services: "Nos services",
      entrust: "Confier mon logement", how: "Notre fonctionnement", about: "À propos",
      blog: "Découvrir Rouen", contact: "Contact", login: "Connexion", account: "Mon espace",
      logout: "Déconnexion", admin: "Administration", owner: "Espace propriétaire", book: "Réserver" },
    common: { search: "Rechercher un logement", from: "À partir de", night: "nuit", nights: "nuits",
      guests: "voyageurs", guest: "voyageur", bedrooms: "chambres", beds: "lits", bathrooms: "sdb",
      viewProperty: "Voir le logement", book: "Réserver", bookNow: "Réserver ce logement",
      destination: "Destination", checkin: "Arrivée", checkout: "Départ", loading: "Chargement…",
      total: "Total", perNight: "par nuit", seeAll: "Voir tous les logements", back: "Retour",
      continue: "Continuer", confirm: "Confirmer et payer", cancel: "Annuler", save: "Enregistrer",
      required: "Champ requis", send: "Envoyer", available: "Disponible", capacity: "Capacité",
      allProperties: "Tous nos logements", email: "Email", phone: "Téléphone", name: "Nom",
      message: "Message", firstName: "Prénom", lastName: "Nom", address: "Adresse" },
    home: { searchTitle: "Trouvez votre séjour à Rouen", propertiesTitle: "Nos logements à Rouen",
      propertiesSub: "Une sélection d'appartements et studios vérifiés par notre équipe locale.",
      whyTitle: "Pourquoi réserver en direct avec NJ Conciergerie",
      whySub: "Le sérieux d'une équipe locale, la chaleur d'un accueil de proximité." },
    why: { direct: "Réservation directe", directD: "Sans intermédiaire, au meilleur prix.",
      secure: "Paiement sécurisé", secureD: "Transactions protégées par Stripe.",
      controlled: "Logements contrôlés", controlledD: "Chaque logement est vérifié par notre équipe.",
      assist: "Assistance voyageurs", assistD: "Une équipe locale disponible 7j/7.",
      local: "Connaissance locale", localD: "Nos meilleures adresses à Rouen.",
      simple: "Réservation simple", simpleD: "Un parcours clair, en quelques clics." },
    times: { checkinInfo: "Arrivée à partir de 17h00", checkoutInfo: "Départ au plus tard à 10h00",
      earlier: "Arriver plus tôt ?", addEarly: "Ajouter une arrivée anticipée",
      later: "Besoin de partir plus tard ?", addLate: "Ajouter un départ tardif",
      standard: "Standard", none: "Aucune", earlyTitle: "Arrivée anticipée", lateTitle: "Départ tardif" },
    booking: { title: "Réservation", step1: "Dates & options", step2: "Vos coordonnées",
      step3: "Récapitulatif & paiement", yourStay: "Votre séjour", extras: "Options supplémentaires",
      summary: "Récapitulatif", nightsLabel: "Nuitées", cleaning: "Frais de ménage",
      touristTax: "Taxe de séjour", discount: "Réduction", earlyCheckin: "Arrivée anticipée",
      lateCheckout: "Départ tardif", cancellation: "Conditions d'annulation",
      acceptTerms: "J'accepte les conditions d'annulation et les CGV",
      payWith: "Payer en toute sécurité", travelerInfo: "Vos informations" },
    cancel: { flexible: "Flexible", moderate: "Modérée", strict: "Stricte", custom: "Personnalisée",
      refunded: "Montant remboursé", retained: "Montant retenu" },
    footer: { tagline: "Conciergerie & location courte durée à Rouen.",
      quickLinks: "Navigation", legal: "Informations légales", contactUs: "Nous contacter",
      rights: "Tous droits réservés.", mentions: "Mentions légales", privacy: "Confidentialité",
      cgv: "CGV", cookies: "Cookies" },
    owner: { title: "Espace propriétaire", myProperties: "Mes logements",
      myReservations: "Mes réservations", revenue: "Revenus", gross: "Chiffre d'affaires",
      commission: "Commission", net: "Revenu net", upcoming: "À venir" },
  },
  en: {
    nav: { properties: "Our stays", concierge: "Concierge", services: "Our services",
      entrust: "Entrust my property", how: "How it works", about: "About",
      blog: "Discover Rouen", contact: "Contact", login: "Sign in", account: "My space",
      logout: "Sign out", admin: "Administration", owner: "Owner area", book: "Book" },
    common: { search: "Search a stay", from: "From", night: "night", nights: "nights",
      guests: "guests", guest: "guest", bedrooms: "bedrooms", beds: "beds", bathrooms: "baths",
      viewProperty: "View property", book: "Book", bookNow: "Book this stay",
      destination: "Destination", checkin: "Check-in", checkout: "Check-out", loading: "Loading…",
      total: "Total", perNight: "per night", seeAll: "See all stays", back: "Back",
      continue: "Continue", confirm: "Confirm & pay", cancel: "Cancel", save: "Save",
      required: "Required field", send: "Send", available: "Available", capacity: "Capacity",
      allProperties: "All our stays", email: "Email", phone: "Phone", name: "Name",
      message: "Message", firstName: "First name", lastName: "Last name", address: "Address" },
    home: { searchTitle: "Find your stay in Rouen", propertiesTitle: "Our stays in Rouen",
      propertiesSub: "A selection of apartments and studios checked by our local team.",
      whyTitle: "Why book directly with NJ Conciergerie",
      whySub: "The reliability of a local team, the warmth of a friendly welcome." },
    why: { direct: "Direct booking", directD: "No middleman, best price.",
      secure: "Secure payment", secureD: "Transactions protected by Stripe.",
      controlled: "Verified stays", controlledD: "Every property is checked by our team.",
      assist: "Guest assistance", assistD: "A local team available 7/7.",
      local: "Local expertise", localD: "Our best addresses in Rouen.",
      simple: "Simple booking", simpleD: "A clear journey, in a few clicks." },
    times: { checkinInfo: "Check-in from 5:00 PM", checkoutInfo: "Check-out by 10:00 AM",
      earlier: "Arrive earlier?", addEarly: "Add early check-in",
      later: "Need to leave later?", addLate: "Add late check-out",
      standard: "Standard", none: "None", earlyTitle: "Early check-in", lateTitle: "Late check-out" },
    booking: { title: "Booking", step1: "Dates & options", step2: "Your details",
      step3: "Summary & payment", yourStay: "Your stay", extras: "Extra options",
      summary: "Summary", nightsLabel: "Nights", cleaning: "Cleaning fee",
      touristTax: "Tourist tax", discount: "Discount", earlyCheckin: "Early check-in",
      lateCheckout: "Late check-out", cancellation: "Cancellation policy",
      acceptTerms: "I accept the cancellation policy and terms",
      payWith: "Pay securely", travelerInfo: "Your information" },
    cancel: { flexible: "Flexible", moderate: "Moderate", strict: "Strict", custom: "Custom",
      refunded: "Refunded amount", retained: "Retained amount" },
    footer: { tagline: "Concierge & short-term rental in Rouen.",
      quickLinks: "Navigation", legal: "Legal", contactUs: "Contact us",
      rights: "All rights reserved.", mentions: "Legal notice", privacy: "Privacy",
      cgv: "Terms", cookies: "Cookies" },
    owner: { title: "Owner area", myProperties: "My properties",
      myReservations: "My reservations", revenue: "Revenue", gross: "Gross revenue",
      commission: "Commission", net: "Net revenue", upcoming: "Upcoming" },
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("nj_lang") || "fr");
  const setLang = useCallback((l) => { localStorage.setItem("nj_lang", l); setLangState(l); }, []);
  const t = useCallback((path) => {
    const parts = path.split(".");
    let cur = translations[lang];
    for (const p of parts) { cur = cur?.[p]; if (cur == null) return path; }
    return cur;
  }, [lang]);
  const tc = useCallback((obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    return obj[lang] || obj.fr || obj.en || "";
  }, [lang]);
  const value = useMemo(() => ({ lang, setLang, t, tc }), [lang, setLang, t, tc]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
